import { AxiosBase } from './base';
import { AxiosRequestBaseConfig, AxiosBaseOptions, AxiosErrorResult } from './types';
import { getSearchParams } from '@/utils/location';
import { v4 } from 'uuid';

const query = getSearchParams();

/**
 * 动态 header 覆盖（供组件化场景使用）
 * 优先级：overrides > URL query
 */
const headerOverrides: Record<string, string> = {};

/**
 * 接口路径前缀 → 域名映射
 * 仅对 qrcode 组件生效，用于组件化场景下的跨域请求
 * 收银台自身项目不使用此映射，走 /api 代理
 */
const domainMap: Record<string, string> = {
  '/order-center': 'https://pay.unipus.cn/api',
};

/**
 * 是否启用域名映射
 * 仅在组件化场景（qrcode）下为 true，收银台自身项目始终为 false
 */
const enableDomainMapping = false;

/**
 * 注入错误 toast 回调，供 Vue 项目传入 message.error
 * React 项目无需调用此方法
 */
let errorNotifier: ((msg: string) => void) | undefined;
export function setErrorNotifier(fn: (msg: string) => void): void {
  errorNotifier = fn;
}

export function setAuthHeaders(headers: { appKey?: string; ssoId?: string; userId?: string }) {
  if (headers.appKey) headerOverrides['appKey'] = headers.appKey;
  if (headers.ssoId) headerOverrides['ssoId'] = headers.ssoId;
  if (headers.userId) headerOverrides['userId'] = headers.userId;
}

/**
 * 覆盖二维码请求的路径前缀对应域名。
 * 仅携带 useQrcodeOrderCenterDomain 标记的请求会使用该映射。
 * 例如: setDomainMap({ '/order-center': 'https://pay-test.unipus.cn/api' })
 */
export function setDomainMap(map: Record<string, string>) {
  Object.assign(domainMap, map);
}

/**
 * 为二维码请求匹配域名，其他请求保持 Axios 默认的 /api。
 */
function resolveDomain(config: AxiosRequestBaseConfig): string | null {
  const { url, useQrcodeOrderCenterDomain } = config;
  if (!url || !useQrcodeOrderCenterDomain) return null;
  // 开发环境走代理，不替换域名
  if (process.env.NODE_ENV === 'development') return null;
  for (const prefix of Object.keys(domainMap)) {
    if (url.startsWith(prefix)) {
      return domainMap[prefix];
    }
  }
  return null;
}

class MyAxios extends AxiosBase {
  constructor(options: AxiosBaseOptions) {
    const { ...axiosOptions } = options;
    super({
      ...axiosOptions,
    });
    // 根据请求路径动态匹配域名（组件化场景）
    this.instance.interceptors.request.use((config) => {
      const domain = resolveDomain(config as AxiosRequestBaseConfig);
      if (domain) {
        config.baseURL = domain;
      }
      return config;
    });
  }
}

// 个性化配置
const createApiClient = (): MyAxios => {
  return new MyAxios({
    baseURL: '/api',
    timeout: 3000,
    withCredentials: true,
    getExtraHeaders: () => ({
      'Content-Type': 'application/json',
      'access-token': headerOverrides['userId'] || query['userId'],
      appKey: headerOverrides['appKey'] || query['appKey'] || 'aigc',
      ssoId: headerOverrides['ssoId'] || query['ssoId'],
      requestId: v4(),
      uid: '0',
    }),
    // 请求前可以做某些操作
    // onBeforeStart: (config: AxiosRequestBaseConfig) => {
    //   console.log('请求前', config);
    // },
    // 可连接日志系统，进行日志记录 centry
    // onLogger: (config: AxiosRequestBaseConfig, info: any, success: boolean) => {
    //   console.log(
    //     'log-----',
    //     config,
    //     'log-----',
    //     'info:',
    //     info,
    //     'success',
    //     success,
    //   );
    // },
    // 处理 error 场景
    onShowError: (msg: string) => errorNotifier?.(msg),
    onError: (err: AxiosErrorResult) => {
      // const online = useOnline();
      // if (!online.value) {
      //   Message.error('怎么没网络');
      // }
      // 重新登录（错误 toast 已由请求层 onShowError 统一弹出，此处不再重复弹出）
      if (err.code === 401) {
        // TODO: 重新登录跳转逻辑
      }
    },
  });
};

export const Axios = createApiClient();
